import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../../config'; // Adjust path if needed
import { useAuth } from '../../context/AuthContext';
import SecurityIcon from "@mui/icons-material/Security";

export default function ZatcaSettingsPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth(); // Access auth context
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const currentUserName = localStorage.getItem('userName') || "";

  // Check if user is authorized (Owner or has Zatca.Manage)
  const isAuthorized = currentUserName.toLowerCase() === 'owner' || hasPermission('Zatca.Manage');

  if (!isAuthorized) {
    return (
      <Box p={3} dir="rtl" textAlign="center" mt={5}>
        <SecurityIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" color="text.secondary" fontWeight="bold">
          عذراً، ليس لديك صلاحية للوصول إلى هذه الصفحة.
        </Typography>
        <Typography variant="body1" color="text.secondary" mt={1}>
          هذه الصفحة مخصصة للمدير العام (Owner) أو من يملك صلاحية إدارة إعدادات ZATCA.
        </Typography>
      </Box>
    );
  }
  
  // Scope Selection
  const [scope, setScope] = useState('company'); // 'company' or 'branch'
  const [selectedBranchId, setSelectedBranchId] = useState('');

  // Form Data
  const [settings, setSettings] = useState({
    environmentType: 'Simulation',
    commonName: '',
    organizationUnitName: '',
    organizationName: '',
    registeredAddress: '',
    businessCategory: '',
    invoiceType: '1100', // Default Simplified
    otp: '',
    
    // Status (Read Only)
    isActive: false,
    isCsrGenerated: false,
    isComplianceChecked: false,
    isProductionOnboarded: false
  });

  // Fetch Branches on mount
  useEffect(() => {
    fetchBranches();
  }, []);

  // Fetch Settings whenever scope/branch changes
  useEffect(() => {
    if (scope === 'branch' && !selectedBranchId) return;
    fetchSettings();
  }, [scope, selectedBranchId]);

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      // Corrected endpoint: /api/Branch (not /branches)
      const res = await axios.get(`${API_BASE_URL}/Branch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const branchParam = scope === 'branch' && selectedBranchId ? `?branchId=${selectedBranchId}` : '';
      
      const res = await axios.get(`${API_BASE_URL}/ZatcaSettings${branchParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data) {
        setSettings(prev => ({ 
          ...prev, 
          ...res.data,
          // Ensure fields are strings to prevent "controlled input" warning
          commonName: res.data.commonName || '',
          organizationUnitName: res.data.organizationUnitName || '',
          organizationName: res.data.organizationName || '',
          registeredAddress: res.data.registeredAddress || '',
          businessCategory: res.data.businessCategory || '',
          otp: res.data.otp || ''
        }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...settings,
        branchId: scope === 'branch' ? selectedBranchId : null
      };
      
      await axios.post(`${API_BASE_URL}/ZatcaSettings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم حفظ الإعدادات بنجاح');
      fetchSettings(); // Refresh
    } catch (err) {
      toast.error('فشل حفظ الإعدادات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchFromProfile = async () => {
    const companyId = localStorage.getItem('companyId');
    if (!companyId) {
      toast.error('لم يتم العثور على معرف الشركة');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/CompanyProfile/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data) {
        const profile = res.data;
        // Build address string similar to ZATCA requirements
        const addressParts = [
          profile.buildingNo,
          profile.streetName,
          profile.district,
          profile.city,
          profile.postalCode
        ].filter(Boolean);
        
        const fullAddress = addressParts.join(', ') || profile.address || '';

        setSettings(prev => ({
          ...prev,
          organizationName: profile.nameAr || profile.nameEn || '',
          registeredAddress: fullAddress,
          businessCategory: profile.activityAr || profile.activityEn || '',
        }));
        
        toast.info('تم جلب البيانات من ملف المنشأة. يرجى مراجعتها وحفظها.');
      }
    } catch (err) {
      console.error(err);
      toast.error('فشل جلب بيانات المنشأة');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCsr = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...settings, // Send full settings to ensure they are saved/used
        branchId: scope === 'branch' ? selectedBranchId : null
      };
      
      await axios.post(`${API_BASE_URL}/ZatcaSettings/generate-csr`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم توليد CSR والمفاتيح بنجاح');
      fetchSettings();
    } catch (err) {
      toast.error('فشل توليد CSR');
    } finally {
      setLoading(false);
    }
  };

  const handleComplianceCheck = async () => {
    if (!settings.otp) {
      toast.warn('الرجاء إدخال رمز OTP');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...settings, // Send full settings to avoid validation errors if any
        branchId: scope === 'branch' ? selectedBranchId : null,
        otp: settings.otp
      };
      
      await axios.post(`${API_BASE_URL}/ZatcaSettings/onboard-compliance`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم اجتياز مرحلة الامتثال بنجاح');
      fetchSettings();
    } catch (err) {
      console.error(err); // Log error for debugging
      toast.error('فشل التحقق من الامتثال: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleProductionOnboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        branchId: scope === 'branch' ? selectedBranchId : null
      };
      
      await axios.post(`${API_BASE_URL}/ZatcaSettings/onboard-production`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم الربط الإنتاجي بنجاح! النظام جاهز للإرسال.');
      fetchSettings();
    } catch (err) {
      toast.error('فشل الربط الإنتاجي');
    } finally {
      setLoading(false);
    }
  };

  // Determine active step
  const getActiveStep = () => {
    if (settings.isProductionOnboarded) return 3; // Done
    if (settings.isComplianceChecked) return 2;
    if (settings.isCsrGenerated) return 1;
    return 0;
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        إعدادات الربط مع هيئة الزكاة والضريبة (ZATCA)
      </Typography>

      {/* Scope Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>نطاق الربط</InputLabel>
              <Select
                value={scope}
                label="نطاق الربط"
                onChange={(e) => setScope(e.target.value)}
              >
                <MenuItem value="company">مستوى الشركة (رئيسي)</MenuItem>
                <MenuItem value="branch">مستوى الفرع</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {scope === 'branch' && (
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>اختر الفرع</InputLabel>
                <Select
                  value={selectedBranchId}
                  label="اختر الفرع"
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                >
                  {branches.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 4 }}>
             {settings.isActive ? (
               <Chip label="نشط - Connected" color="success" />
             ) : (
               <Chip label="غير نشط - Not Connected" color="default" />
             )}
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Side: Configuration Form */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>1. إعدادات CSR (مطلوب)</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>بيئة العمل</InputLabel>
                  <Select
                    value={settings.environmentType}
                    label="بيئة العمل"
                    onChange={(e) => setSettings({...settings, environmentType: e.target.value})}
                    disabled={settings.isCsrGenerated} // Lock after generation usually
                  >
                    <MenuItem value="Simulation">تجريبي (Simulation)</MenuItem>
                    <MenuItem value="Production">إنتاج (Production)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField 
                  fullWidth 
                  label="الاسم الشائع (Common Name)" 
                  placeholder="OTP Reference Number usually"
                  value={settings.commonName}
                  onChange={(e) => setSettings({...settings, commonName: e.target.value})}
                  disabled={settings.isCsrGenerated}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField 
                  fullWidth 
                  label="اسم الوحدة التنظيمية (OU)"  
                  value={settings.organizationUnitName}
                  onChange={(e) => setSettings({...settings, organizationUnitName: e.target.value})}
                  disabled={settings.isCsrGenerated}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField 
                  fullWidth 
                  label="اسم المنظمة (O)"  
                  value={settings.organizationName}
                  onChange={(e) => setSettings({...settings, organizationName: e.target.value})}
                  disabled={settings.isCsrGenerated}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField 
                  fullWidth 
                  label="العنوان المسجل"  
                  value={settings.registeredAddress}
                  onChange={(e) => setSettings({...settings, registeredAddress: e.target.value})}
                  disabled={settings.isCsrGenerated}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField 
                  fullWidth 
                  label="فئة العمل (Business Category)"  
                  value={settings.businessCategory}
                  onChange={(e) => setSettings({...settings, businessCategory: e.target.value})}
                  disabled={settings.isCsrGenerated}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                 <FormControl fullWidth>
                  <InputLabel>نوع الفاتورة</InputLabel>
                  <Select
                    value={settings.invoiceType}
                    label="نوع الفاتورة"
                    onChange={(e) => setSettings({...settings, invoiceType: e.target.value})}
                    disabled={settings.isCsrGenerated}
                  >
                    <MenuItem value="1000">ضريبية قياسية (Standard)</MenuItem>
                    <MenuItem value="1100">ضريبية مبسطة (Simplified)</MenuItem>
                    <MenuItem value="0100">كلاهما</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button 
                    variant="contained" 
                    onClick={handleSaveConfig} 
                    disabled={loading || settings.isCsrGenerated}
                  >
                    حفظ الإعدادات
                  </Button>
                  
                  {!settings.isCsrGenerated && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleFetchFromProfile}
                      disabled={loading}
                    >
                      جلب البيانات من ملف المنشأة
                    </Button>
                  )}
                </Box>
                {settings.isCsrGenerated && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    الإعدادات مقفلة لأنه تم توليد CSR. لإعادة الضبط يرجى التواصل مع الدعم.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right Side: Onboarding Steps */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>2. خطوات الربط والامتثال</Typography>
            
            <Stepper activeStep={getActiveStep()} orientation="vertical">
              {/* Step 1: Generate CSR */}
              <Step>
                <StepLabel>توليد طلب توقيع الشهادة (CSR)</StepLabel>
                <StepContent>
                  <Typography>يجب أولاً توليد المفاتيح وملف CSR بناءً على الإعدادات المدخلة.</Typography>
                  <Box sx={{ mb: 2, mt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleGenerateCsr}
                      disabled={loading}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      توليد CSR
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 2: Compliance */}
              <Step>
                <StepLabel>فحص الامتثال (Compliance)</StepLabel>
                <StepContent>
                  <Typography gutterBottom>
                    أدخل رمز OTP الذي حصلت عليه من بوابة "فاتورة" للبيئة المختارة ({settings.environmentType}).
                  </Typography>
                  <TextField 
                    fullWidth
                    label="رمز OTP (من بوابة فاتورة)"
                    value={settings.otp}
                    onChange={(e) => setSettings({...settings, otp: e.target.value})}
                    sx={{ my: 1 }}
                  />
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleComplianceCheck}
                      disabled={loading || !settings.otp}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      طلب CSID وفحص الامتثال
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 3: Production */}
              <Step>
                <StepLabel>الانتقال للإنتاج (Go Live)</StepLabel>
                <StepContent>
                  <Typography>
                    بعد نجاح فحوصات الامتثال، يمكنك الآن طلب شهادة الإنتاج النهائية (Production CSID).
                  </Typography>
                  <Box sx={{ mb: 2, mt: 1 }}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleProductionOnboard}
                      disabled={loading}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      إصدار شهادة الإنتاج
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              
              {/* Finished */}
              {settings.isProductionOnboarded && (
                <Step active>
                  <StepLabel error={false} icon={'✓'}>تم الربط بنجاح</StepLabel>
                  <StepContent>
                    <Alert severity="success">
                      النظام مرتبط وجاهز لإرسال الفواتير.
                    </Alert>
                  </StepContent>
                </Step>
              )}
            </Stepper>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
